import React, { useState, useEffect } from "react";
import styled from "styled-components";
import JSZip from "jszip";
import toast from "react-hot-toast";
import {
  useGetProfileQuery,
  useUpdateProfileMutation,
  useRegenerateWebhookSecretMutation,
  useGetMerchantTariffsQuery,
  useGet2FAStatusQuery,
  useSetup2FAMutation,
  useVerify2FASetupMutation,
  useDisable2FAMutation,
  useRegenerateBackupCodesMutation,
  useChangePasswordMutation,
} from "../store/api";

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-primary);
  border-radius: 0.75rem 0.75rem 0 0;
  overflow-x: auto;
`;

const Tab = styled.button.withConfig({
  shouldForwardProp: (prop) => !["active"].includes(prop),
})<{ active: boolean }>`
  padding: 1rem 1.5rem;
  border: none;
  background: ${(props) => (props.active ? "var(--bg-card)" : "transparent")};
  color: ${(props) => (props.active ? "var(--text-primary)" : "var(--text-muted)")};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.active ? "var(--accent-primary)" : "transparent")};
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: var(--text-primary);
    background: ${(props) =>
      props.active ? "var(--bg-card)" : "var(--bg-hover)"};
  }
`;

const TabContent = styled.div`
  background: transparent;
  border-radius: 0 0 0.75rem 0.75rem;
  padding: 1rem 0 2rem 0;
  min-height: 400px;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const SettingsCard = styled.div`
  background: var(--bg-primary);
  border-radius: 0.75rem;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Badge = styled.span.withConfig({
  shouldForwardProp: (prop) => !["variant"].includes(prop),
})<{ variant?: "success" | "warning" | "error" }>`
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;

  ${({ variant = "success" }) => {
    switch (variant) {
      case "success":
        return `
          background: var(--status-success-bg);
          color: var(--status-success-text);
        `;
      case "warning":
        return `
          background: var(--status-pending-bg);
          color: var(--status-pending-text);
        `;
      case "error":
        return `
          background: var(--status-error-bg);
          color: var(--status-error-text);
        `;
      default:
        return `
          background: var(--bg-hover);
          color: var(--text-secondary);
        `;
    }
  }}
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.875rem 1rem;
  border: 1px solid var(--border-input);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: var(--bg-card);
  transition: all 0.2s;
  min-height: 2.75rem;

  &:focus {
    outline: none;
    border-color: var(--border-input-focus);
    box-shadow: var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !["variant"].includes(prop),
})<{ variant?: "primary" | "secondary" | "danger" }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  align-self: flex-start;
  margin-top: 1rem;

  ${({ variant = "primary" }) => {
    switch (variant) {
      case "primary":
        return `
          background: var(--accent-primary);
          color: white;
          border: none;
          &:hover {
            background: var(--accent-primary-hover);
          }
        `;
      case "secondary":
        return `
          background: var(--bg-card);
          color: var(--text-primary);
          border: 1px solid var(--border-input);
          &:hover {
            background: var(--bg-primary);
          }
        `;
      case "danger":
        return `
          background: #ef4444;
          color: white;
          border: none;
          &:hover {
            background: #dc2626;
          }
        `;
      default:
        return `
          background: var(--accent-primary);
          color: white;
          border: none;
          &:hover {
            background: var(--accent-primary-hover);
          }
        `;
    }
  }}

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background: var(--status-success-bg);
  color: var(--status-success-text);
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  margin-top: 1rem;
`;

const ErrorMessage = styled.div`
  background: var(--status-error-bg);
  color: var(--status-error-text);
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  margin-top: 1rem;
`;

const InfoBox = styled.div`
  background: var(--status-pending-bg);
  border: 1px solid var(--status-pending-text);
  color: var(--status-pending-text);
  padding: 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: var(--bg-card);

  &:focus {
    outline: none;
    border-color: var(--border-input-focus);
    box-shadow: var(--focus-ring);
  }
`;

const SelectableExportButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !["selected", "disabled"].includes(prop),
})<{
  selected?: boolean;
  disabled?: boolean;
}>`
  padding: 0.75rem 1rem;
  border: 2px solid ${(props) => (props.selected ? "var(--accent-primary)" : "var(--border-color)")};
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;
  background: ${(props) => (props.selected ? "var(--accent-primary-light)" : "var(--bg-card)")};
  color: ${(props) => (props.selected ? "var(--accent-primary)" : "var(--text-primary)")};

  &:hover:not(:disabled) {
    background: ${(props) => (props.selected ? "var(--accent-primary-light)" : "var(--bg-hover)")};
    border-color: ${(props) => (props.selected ? "var(--accent-primary)" : "var(--border-hover)")};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TariffsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const TableHeader = styled.th`
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-divider);
  background: var(--bg-primary);
`;

const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-divider);
  font-size: 0.875rem;
  color: var(--text-primary);
`;

function Settings() {
  const [activeTab, setActiveTab] = useState<"general" | "tariffs" | "security">("general");

  const { data: profile, isLoading } = useGetProfileQuery();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateProfileMutation();
  const [regenerateSecret, { isLoading: isRegenerating }] = useRegenerateWebhookSecretMutation();
  const [showSecret, setShowSecret] = useState(false);
  const { data: merchantTariffs, isLoading: tariffsLoading } =
    useGetMerchantTariffsQuery(profile?.merchant?.id || "", {
      skip: !profile?.merchant?.id,
    });

  // 2FA
  const { data: twoFAStatus, refetch: refetch2FA } = useGet2FAStatusQuery();
  const [setup2FA] = useSetup2FAMutation();
  const [verify2FASetup] = useVerify2FASetupMutation();
  const [disable2FA] = useDisable2FAMutation();
  const [regenerateBackupCodes] = useRegenerateBackupCodesMutation();

  const [qrData, setQrData] = useState<{ qrCode: string; secret: string } | null>(null);
  const [setupCode, setSetupCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [disableForm, setDisableForm] = useState({ password: "", code: "" });
  const [regenCode, setRegenCode] = useState("");
  const [twoFALoading, setTwoFALoading] = useState(false);

  // Change password
  const [changePassword] = useChangePasswordMutation();
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [formData, setFormData] = useState({
    callbackUrl: "",
  });

  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  // Export form state
  const [exportForm, setExportForm] = useState({
    dateFrom: "",
    dateTo: "",
    format: "CSV",
  });

  const [selectedExportTypes, setSelectedExportTypes] = useState<Set<string>>(
    new Set()
  );
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (profile?.merchant) {
      setFormData({
        callbackUrl: profile.merchant.callbackUrl || "",
      });
    }
  }, [profile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");

    try {
      await updateProfile(formData).unwrap();
      setMessage("Настройки успешно сохранены!");
      setIsError(false);
    } catch (error) {
      setMessage("Ошибка при сохранении настроек");
      setIsError(true);
    }
  };

  const handleExportFormChange = (field: string, value: string) => {
    setExportForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleExportType = (type: string) => {
    setSelectedExportTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const handleBatchExport = async () => {
    if (selectedExportTypes.size === 0) {
      setMessage("Выберите тип данных для экспорта");
      setIsError(true);
      return;
    }

    setIsExporting(true);
    setMessage("");
    setIsError(false);

    try {
      const exportTypes = Array.from(selectedExportTypes);
      const files: { [key: string]: string } = {};

      // Собираем данные для каждого выбранного типа
      for (const type of exportTypes) {
        const data = await fetchDataForType(type);
        const content = formatDataAsCSV(data, type);
        files[`${type.toLowerCase()}.csv`] = content;
      }

      // Если выбран только один тип, скачиваем файл напрямую
      if (exportTypes.length === 1) {
        const type = exportTypes[0];
        const filename = `${type.toLowerCase()}_${new Date().toISOString().split("T")[0]}.csv`;
        downloadFile(files[`${type.toLowerCase()}.csv`], filename);
      } else {
        // Если несколько типов, создаем архив
        const zip = new JSZip();

        for (const [filename, content] of Object.entries(files)) {
          zip.file(filename, content);
        }

        const zipContent = await zip.generateAsync({ type: "blob" });
        const zipFilename = `export_${new Date().toISOString().split("T")[0]}.zip`;
        downloadBlob(zipContent, zipFilename);
      }

      setMessage("Экспорт успешно выполнен!");
      setIsError(false);
    } catch (error: any) {
      console.error("Export error:", error);
      setMessage(error?.message || "Ошибка при экспорте данных");
      setIsError(true);
    } finally {
      setIsExporting(false);
    }
  };

  const fetchDataForType = async (type: string) => {
    const baseUrl =
      process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    const token = localStorage.getItem("token");
    const dateFrom = exportForm.dateFrom;
    const dateTo = exportForm.dateTo;

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    switch (type) {
      case "ORDERS": {
        const params = new URLSearchParams({
          limit: "10000",
        });
        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);

        const response = await fetch(`${baseUrl}/orders?${params}`, {
          headers,
        });
        if (!response.ok) throw new Error("Ошибка загрузки ордеров");
        const data = await response.json();
        return data.data || [];
      }

      case "BALANCE_HISTORY": {
        const params = new URLSearchParams({
          limit: "10000",
        });
        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);

        const response = await fetch(`${baseUrl}/balance/history?${params}`, {
          headers,
        });
        if (!response.ok) throw new Error("Ошибка загрузки истории баланса");
        const data = await response.json();
        return Array.isArray(data) ? data : data.data || [];
      }

      case "WITHDRAWALS": {
        const params = new URLSearchParams({
          limit: "10000",
        });
        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);

        const response = await fetch(`${baseUrl}/withdrawals?${params}`, {
          headers,
        });
        if (!response.ok) throw new Error("Ошибка загрузки выводов");
        const data = await response.json();
        return data.data || [];
      }

      case "APPEALS": {
        const params = new URLSearchParams({
          limit: "10000",
        });
        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);

        const response = await fetch(`${baseUrl}/appeals?${params}`, {
          headers,
        });
        if (!response.ok) throw new Error("Ошибка загрузки апелляций");
        const data = await response.json();
        return data.data || [];
      }

      default:
        throw new Error(`Неизвестный тип данных: ${type}`);
    }
  };

  const formatDataAsCSV = (data: any[], type: string): string => {
    if (!data || data.length === 0) {
      return `Нет данных для экспорта (${type})`;
    }

    switch (type) {
      case "ORDERS": {
        const headers = [
          "#ID",
          "ID_merch",
          "ID_client",
          "Дата создания",
          "Дата оплаты",
          "Метод",
          "Сумма",
          "Курс",
          "Сумма (USDT)",
          "Статус",
        ];
        const rows = data.map((order: any) => [
          order.id || "",
          order.invId || "",
          order.clientId || "",
          order.createdAt || "",
          order.paidAt || "",
          order.bank || order.method || "Card",
          order.currency === "RUB"
            ? `${order.amount} ₽`
            : `${order.amount} ${order.currency}`,
          order.exchangeRate || "",
          order.usdtAmount ? `${order.usdtAmount} USDT` : "",
          order.status || "",
        ]);
        return [headers, ...rows]
          .map((row) => row.map((cell) => `"${cell}"`).join(","))
          .join("\n");
      }

      case "BALANCE_HISTORY": {
        const headers = [
          "ID",
          "Сумма",
          "Баланс до",
          "Баланс после",
          "Описание",
          "Дата",
        ];
        const rows = data.map((item: any) => [
          item.id || "",
          item.amount || "",
          item.balanceBefore || "",
          item.balanceAfter || "",
          item.description || "",
          item.createdAt || "",
        ]);
        return [headers, ...rows]
          .map((row) => row.map((cell) => `"${cell}"`).join(","))
          .join("\n");
      }

      case "WITHDRAWALS": {
        const headers = [
          "ID",
          "Сумма",
          "Валюта",
          "Адрес кошелька",
          "Статус",
          "Комментарий",
          "Админ комментарий",
          "TX Hash",
          "Создан",
          "Обновлен",
        ];
        const rows = data.map((withdrawal: any) => [
          withdrawal.id || "",
          withdrawal.amount || "",
          withdrawal.currency || "",
          withdrawal.walletAddress || "",
          withdrawal.status || "",
          withdrawal.comment || "",
          withdrawal.adminComment || "",
          withdrawal.txHash || "",
          withdrawal.createdAt || "",
          withdrawal.updatedAt || "",
        ]);
        return [headers, ...rows]
          .map((row) => row.map((cell) => `"${cell}"`).join(","))
          .join("\n");
      }

      case "APPEALS": {
        const headers = [
          "ID",
          "ID ордера",
          "Причина",
          "Комментарий",
          "Статус",
          "Создан",
          "Обновлен",
        ];
        const rows = data.map((appeal: any) => [
          appeal.id || "",
          appeal.orderId || "",
          appeal.reason || "",
          appeal.comment || "",
          appeal.status || "",
          appeal.createdAt || "",
          appeal.updatedAt || "",
        ]);
        return [headers, ...rows]
          .map((row) => row.map((cell) => `"${cell}"`).join(","))
          .join("\n");
      }

      default:
        return `Неизвестный тип данных: ${type}`;
    }
  };

  const downloadFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    downloadBlob(blob, filename);
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderGeneralSettings = () => (
    <SettingsGrid>
      <SettingsCard>
        <CardTitle>Webhook настройки</CardTitle>
        <InfoBox>
          <strong>Важно:</strong> Webhook URL используется для отправки
          уведомлений о статусе платежей. Убедитесь, что ваш сервер может
          принимать POST запросы на указанный адрес.
        </InfoBox>

        <form onSubmit={handleSubmit}>
          <FormGroup>
              <Label>Webhook URL</Label>
              <Input
                type="url"
                name="callbackUrl"
                value={formData.callbackUrl}
                onChange={handleInputChange}
                placeholder="https://yoursite.com/webhook"
              />
              <small style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>
                URL для отправки уведомлений о статусе платежей
              </small>
            </FormGroup>

            <FormGroup>
              <Label>Webhook Secret</Label>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <Input
                  type={showSecret ? "text" : "password"}
                  value={profile?.merchant?.callbackSecret || "Не сгенерирован"}
                  readOnly
                  style={{ background: "var(--bg-input-disabled)", cursor: "default", flex: 1, marginBottom: 0 }}
                />
                <span
                  className="material-icons-outlined"
                  title={showSecret ? "Скрыть" : "Показать"}
                  onClick={() => setShowSecret(!showSecret)}
                  style={{ cursor: "pointer", color: "var(--text-muted)", fontSize: "22px", userSelect: "none" }}
                >
                  {showSecret ? "visibility_off" : "visibility"}
                </span>
                {profile?.merchant?.callbackSecret && (
                  <span
                    className="material-icons-outlined"
                    title="Копировать"
                    onClick={() => {
                      navigator.clipboard.writeText(profile.merchant.callbackSecret);
                      toast.success("Скопировано");
                    }}
                    style={{ cursor: "pointer", color: "var(--text-muted)", fontSize: "22px", userSelect: "none" }}
                  >
                    content_copy
                  </span>
                )}
                <span
                  className="material-icons-outlined"
                  title="Перегенерировать"
                  onClick={async () => {
                    if (isRegenerating) return;
                    if (window.confirm("Перегенерировать Webhook Secret? Текущий секрет станет недействительным.")) {
                      try {
                        await regenerateSecret().unwrap();
                        toast.success("Webhook Secret перегенерирован");
                        setShowSecret(true);
                      } catch (err: any) {
                        toast.error(err?.data?.message || "Ошибка перегенерации");
                      }
                    }
                  }}
                  style={{ cursor: isRegenerating ? "wait" : "pointer", color: "var(--text-muted)", fontSize: "22px", userSelect: "none" }}
                >
                  autorenew
                </span>
              </div>
              <small style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>
                Используется для подписи webhook запросов (HMAC-SHA256)
              </small>
            </FormGroup>

          <Button type="submit" disabled={isUpdating}>
            {isUpdating ? "Сохранение..." : "Сохранить настройки"}
          </Button>

          {message &&
            (isError ? (
              <ErrorMessage>{message}</ErrorMessage>
            ) : (
              <SuccessMessage>{message}</SuccessMessage>
            ))}
        </form>
      </SettingsCard>

      <SettingsCard
        style={{ display: "flex", flexDirection: "column", height: "100%" }}
      >
        <CardTitle>Экспорт данных</CardTitle>

        <FormGroup>
          <Label>Период выгрузки</Label>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <Input
              type="date"
              style={{ width: "auto" }}
              value={exportForm.dateFrom}
              onChange={(e) =>
                handleExportFormChange("dateFrom", e.target.value)
              }
            />
            <span style={{ color: "var(--text-secondary)" }}>—</span>
            <Input
              type="date"
              style={{ width: "auto" }}
              value={exportForm.dateTo}
              onChange={(e) => handleExportFormChange("dateTo", e.target.value)}
            />
          </div>
        </FormGroup>

        <FormGroup>
          <Label>Формат файла</Label>
          <Select
            value={exportForm.format}
            onChange={(e) => handleExportFormChange("format", e.target.value)}
          >
            <option value="CSV">CSV</option>
            <option value="XLSX">Excel (XLSX)</option>
            <option value="JSON">JSON</option>
          </Select>
        </FormGroup>

        {/* Выбираемые кнопки экспорта - только доступные для мерчанта */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "0.5rem",
            marginTop: "auto",
            paddingTop: "1rem",
          }}
        >
          <SelectableExportButton
            selected={selectedExportTypes.has("ORDERS")}
            onClick={() => toggleExportType("ORDERS")}
            disabled={isExporting}
          >
            Ордера
          </SelectableExportButton>
          <SelectableExportButton
            selected={selectedExportTypes.has("BALANCE_HISTORY")}
            onClick={() => toggleExportType("BALANCE_HISTORY")}
            disabled={isExporting}
          >
            История баланса
          </SelectableExportButton>
          <SelectableExportButton
            selected={selectedExportTypes.has("WITHDRAWALS")}
            onClick={() => toggleExportType("WITHDRAWALS")}
            disabled={isExporting}
          >
            Выводы
          </SelectableExportButton>
          <SelectableExportButton
            selected={selectedExportTypes.has("APPEALS")}
            onClick={() => toggleExportType("APPEALS")}
            disabled={isExporting}
          >
            Апелляции
          </SelectableExportButton>
        </div>

        {/* Кнопка загрузки */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "1rem",
            paddingTop: "1rem",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            variant="primary"
            onClick={handleBatchExport}
            disabled={isExporting || selectedExportTypes.size === 0}
            style={{ minWidth: "200px" }}
          >
            {isExporting ? "Загрузка..." : "Загрузить"}
          </Button>
        </div>
      </SettingsCard>
    </SettingsGrid>
  );

  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error("Заполните все поля");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error("Новый пароль должен содержать минимум 6 символов");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Новый пароль и подтверждение не совпадают");
      return;
    }
    setPasswordLoading(true);
    try {
      await changePassword(passwordForm).unwrap();
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      toast.success("Пароль успешно изменён");
    } catch (err: any) {
      toast.error(err?.data?.message || "Ошибка при смене пароля");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const result = await setup2FA().unwrap();
      setQrData({ qrCode: result.qrCode, secret: result.secret });
    } catch (err: any) {
      toast.error(err?.data?.message || "Ошибка при генерации QR-кода");
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (setupCode.length < 6) { toast.error("Введите 6-значный код"); return; }
    setTwoFALoading(true);
    try {
      const result = await verify2FASetup({ code: setupCode }).unwrap();
      setBackupCodes(result.backupCodes);
      setQrData(null);
      setSetupCode("");
      refetch2FA();
      toast.success("2FA успешно активирована!");
    } catch (err: any) {
      toast.error(err?.data?.message || "Неверный код");
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disableForm.password || !disableForm.code) { toast.error("Заполните все поля"); return; }
    setTwoFALoading(true);
    try {
      await disable2FA(disableForm).unwrap();
      setDisableForm({ password: "", code: "" });
      refetch2FA();
      toast.success("2FA отключена");
    } catch (err: any) {
      toast.error(err?.data?.message || "Ошибка");
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleRegenCodes = async () => {
    if (regenCode.length < 6) { toast.error("Введите код"); return; }
    setTwoFALoading(true);
    try {
      const result = await regenerateBackupCodes({ code: regenCode }).unwrap();
      setBackupCodes(result.backupCodes);
      setRegenCode("");
      refetch2FA();
      toast.success("Резервные коды перегенерированы");
    } catch (err: any) {
      toast.error(err?.data?.message || "Ошибка");
    } finally {
      setTwoFALoading(false);
    }
  };

  const renderSecuritySettings = () => (
    <SettingsGrid>
      <SettingsCard>
        <CardTitle>Смена пароля</CardTitle>
        <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginBottom: "1rem" }}>
          Для смены пароля введите текущий пароль и новый пароль дважды.
        </div>
        <FormGroup>
          <Label>Текущий пароль</Label>
          <Input type="password" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))} placeholder="Введите текущий пароль" />
        </FormGroup>
        <FormGroup>
          <Label>Новый пароль</Label>
          <Input type="password" value={passwordForm.newPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))} placeholder="Минимум 6 символов" />
        </FormGroup>
        <FormGroup>
          <Label>Подтверждение нового пароля</Label>
          <Input type="password" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))} placeholder="Повторите новый пароль" />
        </FormGroup>
        <Button variant="primary" onClick={handleChangePassword} disabled={passwordLoading} style={{ marginTop: "0.75rem" }}>
          {passwordLoading ? "Сохранение..." : "Изменить пароль"}
        </Button>
      </SettingsCard>

      <SettingsCard>
        <CardTitle>Двухфакторная аутентификация (2FA)</CardTitle>

        {twoFAStatus?.enabled ? (
          <>
            <Badge variant="success">2FA активна</Badge>
            <div style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "var(--text-secondary)" }}>
              Резервных кодов: {twoFAStatus.backupCodesRemaining}
            </div>

            <div style={{ marginTop: "1.5rem" }}>
              <CardTitle style={{ fontSize: "0.95rem" }}>Перегенерировать резервные коды</CardTitle>
              <FormGroup>
                <Label>Текущий код 2FA</Label>
                <Input value={regenCode} onChange={(e) => setRegenCode(e.target.value.replace(/[^0-9]/g, ""))} maxLength={6} placeholder="000000" />
              </FormGroup>
              <Button onClick={handleRegenCodes} disabled={twoFALoading} style={{ marginTop: "0.75rem" }}>Перегенерировать</Button>
            </div>

            <div style={{ marginTop: "2rem", borderTop: "1px solid #e2e8f0", paddingTop: "1.5rem" }}>
              <CardTitle style={{ fontSize: "0.95rem", color: "#dc2626" }}>Отключить 2FA</CardTitle>
              <FormGrid>
                <FormGroup>
                  <Label>Пароль</Label>
                  <Input type="password" value={disableForm.password} onChange={(e) => setDisableForm((p) => ({ ...p, password: e.target.value }))} placeholder="Ваш пароль" />
                </FormGroup>
                <FormGroup>
                  <Label>Код 2FA</Label>
                  <Input value={disableForm.code} onChange={(e) => setDisableForm((p) => ({ ...p, code: e.target.value.replace(/[^0-9]/g, "") }))} maxLength={6} placeholder="000000" />
                </FormGroup>
              </FormGrid>
              <Button variant="danger" onClick={handleDisable2FA} disabled={twoFALoading} style={{ marginTop: "0.75rem" }}>Отключить 2FA</Button>
            </div>
          </>
        ) : qrData ? (
          <>
            <InfoBox>Отсканируйте QR-код в приложении Google Authenticator, затем введите 6-значный код для подтверждения.</InfoBox>
            <div style={{ textAlign: "center", margin: "1rem 0" }}>
              <img src={qrData.qrCode} alt="QR Code" style={{ width: 200, height: 200, borderRadius: "0.5rem" }} />
            </div>
            <div style={{ textAlign: "center", fontSize: "0.75rem", color: "var(--text-secondary)", wordBreak: "break-all", marginBottom: "1rem" }}>
              Или введите вручную: <strong>{qrData.secret}</strong>
            </div>
            <FormGroup>
              <Label>Код из приложения</Label>
              <Input value={setupCode} onChange={(e) => setSetupCode(e.target.value.replace(/[^0-9]/g, ""))} maxLength={6} placeholder="000000" style={{ textAlign: "center", fontSize: "1.25rem", letterSpacing: "0.3rem" }} />
            </FormGroup>
            <Button onClick={handleVerify2FA} disabled={twoFALoading}>Подтвердить и активировать</Button>
          </>
        ) : (
          <>
            <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginBottom: "1rem" }}>
              Двухфакторная аутентификация добавляет дополнительный уровень защиты вашего аккаунта. При входе помимо пароля потребуется код из приложения Google Authenticator.
            </div>
            <Button onClick={handleSetup2FA} disabled={twoFALoading}>
              {twoFALoading ? "Загрузка..." : "Настроить 2FA"}
            </Button>
          </>
        )}
      </SettingsCard>

      {backupCodes && (
        <SettingsCard>
          <CardTitle>Резервные коды</CardTitle>
          <InfoBox>
            <strong>Важно!</strong> Сохраните эти коды в безопасном месте. Каждый код можно использовать только один раз для входа, если вы потеряете доступ к аутентификатору.
          </InfoBox>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", fontFamily: "monospace", fontSize: "0.95rem" }}>
            {backupCodes.map((code, i) => (
              <div key={i} style={{ padding: "0.5rem", background: "#f1f5f9", borderRadius: "0.25rem", textAlign: "center" }}>{code}</div>
            ))}
          </div>
          <Button variant="secondary" onClick={() => setBackupCodes(null)} style={{ marginTop: "1rem" }}>Скрыть коды</Button>
        </SettingsCard>
      )}
    </SettingsGrid>
  );

  const renderTariffsSettings = () => (
    <SettingsGrid>
      <SettingsCard>
        <CardTitle>Тарифы мерчанта</CardTitle>
        <InfoBox>Ваши текущие тарифы и комиссии по методам оплаты.</InfoBox>

        {tariffsLoading ? (
          <div
            style={{ textAlign: "center", padding: "2rem", color: "var(--text-secondary)" }}
          >
            Загрузка тарифов...
          </div>
        ) : merchantTariffs?.data && merchantTariffs.data.length > 0 ? (
          <TariffsTable>
            <thead>
              <tr>
                <TableHeader>Название</TableHeader>
                <TableHeader>Тип</TableHeader>
                <TableHeader>Метод</TableHeader>
                <TableHeader>Банк</TableHeader>
                <TableHeader>Ставка (%)</TableHeader>
                <TableHeader>Диапазон сумм</TableHeader>
                <TableHeader>Статус</TableHeader>
              </tr>
            </thead>
            <tbody>
              {merchantTariffs.data.map((tariff: any) => (
                <tr key={tariff.id}>
                  <TableCell>{tariff.name || "—"}</TableCell>
                  <TableCell>{tariff.type || "—"}</TableCell>
                  <TableCell>{tariff.method || "—"}</TableCell>
                  <TableCell>{tariff.bankMethod || "Любой"}</TableCell>
                  <TableCell>{tariff.feePercentage}%</TableCell>
                  <TableCell>
                    {tariff.minAmount} -{" "}
                    {tariff.maxAmount ? `${tariff.maxAmount} ₽` : "∞ ₽"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={tariff.isActive ? "success" : "error"}>
                      {tariff.isActive ? "Активен" : "Неактивен"}
                    </Badge>
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </TariffsTable>
        ) : (
          <div
            style={{ textAlign: "center", padding: "2rem", color: "var(--text-secondary)" }}
          >
            Тарифы не назначены. Обратитесь к администратору.
          </div>
        )}
      </SettingsCard>
    </SettingsGrid>
  );

  if (isLoading) {
    return (
      <SettingsContainer>
        <div style={{ textAlign: "center", padding: "2rem", color: "var(--text-secondary)" }}>
          Загрузка настроек...
        </div>
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer>
      <TabsContainer>
        <Tab
          active={activeTab === "general"}
          onClick={() => setActiveTab("general")}
        >
          Общие настройки
        </Tab>
        <Tab
          active={activeTab === "security"}
          onClick={() => setActiveTab("security")}
        >
          Безопасность
        </Tab>
        <Tab
          active={activeTab === "tariffs"}
          onClick={() => setActiveTab("tariffs")}
        >
          Тарифы
        </Tab>
      </TabsContainer>

      <TabContent>
        {activeTab === "general" && renderGeneralSettings()}
        {activeTab === "security" && renderSecuritySettings()}
        {activeTab === "tariffs" && renderTariffsSettings()}
      </TabContent>
    </SettingsContainer>
  );
}

export default Settings;
